import { Injectable } from '@angular/core';
import { PreferenceStorageService } from './preference-storage.service';
import { FilterChip } from '@compumark/brand-context-components';
import { FilterOperationService } from './filter-operation.service';

@Injectable()
export class FilterLockService {
  static readonly LOCKED_FILTER_KEY = 'lockedFilterChips';
  static readonly LOCKED_CUSTOM_FILTER_KEY = 'lockedCustomFilterChips';
  private lockableFields = new Set<string>();
  private lockedChips = new Map<string, FilterChip>();
  private identifier!: string;
  private contentType!: string;

  constructor(
    private preferenceStorageService: PreferenceStorageService,
    private filterOperationService: FilterOperationService,
  ) {
    const lockableFieldsConfig = [
      'trademarkJurisdictionCode',
      'trademarkJurisdictionName',
      'trademarkStatus',
      'trademarkName',
      'name',
      'pattern',
      'classes',
      'orderType',
      'analystName',
      'taskDescription',
      'taskStatus.label',
      'deliveryDate',
      'qualityReviewStatus',
      'inactiveDate',
    ];

    this.lockableFields = new Set(lockableFieldsConfig);
  }
  public getLockableFields(): string[] {
    return Array.from(this.lockableFields);
  }
  private getChipKey(chip: FilterChip): string {
    const value = this.getChipValue(chip);
    return `${chip.field}:${value}:${this.contentType}`;
  }

  private getChipValue(chip: FilterChip): string {
    return typeof chip.value === 'string' && chip.value.trim().length > 0
      ? chip.value
      : chip.valueLabel ?? '';
  }

  public setLockedFilterChips(
    filterChip: FilterChip,
    identifier: string,
    contentType: string,
  ): void {
    if (filterChip.locked !== undefined) {
      const key = this.getChipKey(filterChip);
      const action = filterChip.locked ? 'set' : 'delete';
      this.lockedChips[action](key, filterChip);
      this.saveLockedFilterChips(identifier, contentType);
    }
  }

  public getLockedChips(): FilterChip[] {
    return Array.from(this.lockedChips.values());
  }

  public applyLockOnChips(filterChips: FilterChip[]): FilterChip[] {
    filterChips.forEach((chip) => {
      chip.locked = this.lockedChips.has(this.getChipKey(chip));
    });
    return filterChips;
  }

  public clearChip(filterChip: FilterChip): void {
    this.lockedChips.delete(this.getChipKey(filterChip));
  }

  public removeUnlockedFilterModel(currentFilterModel: {
    [key: string]: any;
  }): any {
    const lockableFields = this.getLockableFields();
    const lockedChips = this.getLockedChips();

    this.filterOperationService.removeUnlockedFilters(
      currentFilterModel,
      lockedChips,
      lockableFields,
    );
    return currentFilterModel;
  }
  public saveLockedFilterChips(identifier: string, contentType: string): void {
    const chips = Array.from(this.lockedChips.values());
    const filterChip = this.filterChipsByCustomFlag(chips, false);
    const customFilterChip = this.filterChipsByCustomFlag(chips, true);

    this.savePreference(
      this.getLockFilterPreferenceKey(identifier, contentType),
      filterChip,
    );
    this.savePreference(
      this.getCustomLockFilterPreferenceKey(identifier),
      customFilterChip,
    );
  }
  private filterChipsByCustomFlag(
    chips: FilterChip[],
    isCustom: boolean,
  ): FilterChip[] {
    return chips.filter((chip) => {
      if (isCustom) {
        return chip.customFilter === true;
      } else {
        return chip.customFilter === false || chip.customFilter === undefined;
      }
    });
  }

  private savePreference(key: string, chips: FilterChip[]): void {
    this.preferenceStorageService.setPreference(key, chips);
  }

  public initializeLockedChips(
    savedLockedChips: FilterChip[],
    identifier: string,
    contentType: string,
  ): void {
    this.identifier = identifier;
    this.contentType = contentType;
    if (!savedLockedChips) {
      return;
    }

    const newLockedChips = new Map<string, FilterChip>();
    savedLockedChips.forEach((chip) => {
      if (chip.locked) {
        newLockedChips.set(this.getChipKey(chip), chip);
      }
    });

    this.lockedChips.forEach((_, key) => {
      if (!newLockedChips.has(key)) {
        this.lockedChips.delete(key);
      }
    });

    this.lockedChips = newLockedChips;
  }

  public updateLockedChipsList(
    filterChips: FilterChip[],
    isGridInitInProgress: boolean,
  ): void {
    filterChips.forEach((filterChip) => {
      this.setLockedFilterChips(filterChip, this.identifier, this.contentType);
    });
    if (!isGridInitInProgress) {
      this.removeUnlockedChips(filterChips);
    }
  }

  public getLockFilterPreferenceKey(
    identifier: string,
    contentType: string,
  ): string {
    return `${FilterLockService.LOCKED_FILTER_KEY}${contentType}${identifier}`;
  }
  public getCustomLockFilterPreferenceKey(identifier: string): string {
    return `${FilterLockService.LOCKED_CUSTOM_FILTER_KEY}${identifier}`;
  }
  private removeUnlockedChips(filterChips: FilterChip[]): void {
    this.lockedChips.forEach((chip) => {
      if (
        !filterChips.some(
          (filterChip) => this.getChipKey(filterChip) === this.getChipKey(chip),
        )
      ) {
        this.lockedChips.delete(this.getChipKey(chip));
        this.saveLockedFilterChips(this.identifier, this.contentType);
      }
    });
  }
}
